import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Typography, 
  Button,
  Row,
  Col,
  Statistic,
  Table,
  message,
  Input,
  Select,
  Tag,
  Progress,
  Skeleton,
  Modal,
  Form,
  Tooltip
} from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  SyncOutlined,
  DashboardOutlined,
  WarningOutlined,
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  EditOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import api from '../../services/api';
import { debounce } from 'lodash';

const { Title, Text } = Typography;
const { Option } = Select;

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px rgba(102, 126, 234, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.8);
  }
`;

// Styled components
const PageContainer = styled.div`
  background-color: #f5f7fa;
  min-height: 100vh;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-radius: 0 0 10px 10px;

  h1 {
    font-size: 2.2rem;
    margin-bottom: 0.25rem;
    color: white;
  }
  
  p {
    font-size: 1.1rem;
    opacity: 0.9;
    margin: 0;
    color: white;
  }
`;

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled(Card)<{ 
  cardType?: 'total' | 'correct' | 'incorrect' | 'pending';
  isActive?: boolean;
  isClickable?: boolean;
}>`
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInUp} 0.6s ease-out;
  cursor: ${props => props.isClickable ? 'pointer' : 'default'};
  position: relative;
  overflow: hidden;
  
  background: ${props => props.isActive ? 
    `linear-gradient(135deg, ${
      props.cardType === 'correct' ? '#10b981' : 
      props.cardType === 'incorrect' ? '#ef4444' : 
      props.cardType === 'pending' ? '#f59e0b' : '#6366f1'
    }15, ${
      props.cardType === 'correct' ? '#10b981' : 
      props.cardType === 'incorrect' ? '#ef4444' : 
      props.cardType === 'pending' ? '#f59e0b' : '#6366f1'
    }05)` : 'white'
  };
  
  border: 2px solid ${props => props.isActive ? 
    (props.cardType === 'correct' ? '#10b981' : 
     props.cardType === 'incorrect' ? '#ef4444' : 
     props.cardType === 'pending' ? '#f59e0b' : '#6366f1') : 'transparent'
  };
  
  border-left: 4px solid ${props => {
    switch(props.cardType) {
      case 'correct': return '#10b981';
      case 'incorrect': return '#ef4444';
      case 'pending': return '#f59e0b';
      case 'total': return '#6366f1';
      default: return '#6366f1';
    }
  }};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      ${props => {
        switch(props.cardType) {
          case 'correct': return '#10b981';
          case 'incorrect': return '#ef4444';
          case 'pending': return '#f59e0b';
          case 'total': return '#6366f1';
          default: return '#6366f1';
        }
      }}10, transparent 50%);
    opacity: ${props => props.isActive ? 1 : 0};
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 8px 30px rgba(0,0,0,0.15);
    
    ${props => props.isClickable && css`
      animation: ${glow} 2s ease-in-out infinite;
    `}
  }
  
  &:active {
    transform: translateY(-2px) scale(1.01);
  }
  
  .ant-card-body {
    padding: 1.5rem 1rem;
    position: relative;
    z-index: 1;
  }
  
  .ant-statistic-title {
    font-size: 0.85rem;
    color: ${props => props.isActive ? '#333' : '#666'};
    text-transform: uppercase;
    letter-spacing: 1.2px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .ant-statistic-title {
      span {
        width: 35px;
        height: 35px;
        background-color: ${props => {
          switch(props.cardType) {
            case 'correct': return '#10b981';
            case 'incorrect': return '#ef4444';
            case 'pending': return '#f59e0b';
            case 'total': return '#6366f1';
            default: return '#6366f1';
          }
        }};
        color: rgb(255, 255, 255);
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 4px;
      }
  }
  
  .ant-statistic-content {
    .ant-statistic-content-value {
      font-size: 2.8rem;
      font-weight: 700;
      color: ${props => {
        switch(props.cardType) {
          case 'correct': return '#10b981';
          case 'incorrect': return '#ef4444';
          case 'pending': return '#f59e0b';
          case 'total': return '#6366f1';
          default: return '#6366f1';
        }
      }};
      text-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .ant-statistic-content-suffix {
      font-size: 0.75rem;
      color: #888;
      margin: 0;
      display: block;
      line-height: 1.4;
    }
  }
`;

const FilterBar = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1px solid #f0f0f0;
`;

const FilterTags = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const TableContainer = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 1.5rem;
  .ant-table {
    border: 1px solid #ddd;
  }
  
  .ant-card-head-title {
    font-size: 1.5rem;
    color: #333;
  }
  
  .ant-card-body {
    padding: 1rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f4f6;
    border-top: 4px solid #6366f1;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LastUpdate = styled.div`
  text-align: right;
  color: #666;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

interface DiagnosysFilters {
  status: 'all' | 'correct' | 'incorrect';
  country: string;
  state: string;
  search: string;
  page: number;
  pageSize: number;
}

interface DashboardData {
  overview: {
    total_locations: number;
    correct_locations: number;
    incorrect_locations: number;
    not_validated: number;
    accuracy_rate: number;
  };
  validation_trend: Array<{
    date: string;
    correct: number;
    incorrect: number;
  }>;
  country_breakdown: Array<{
    country: string;
    correct: number;
    incorrect: number;
  }>;
  state_breakdown: Array<{
    state: string;
    accuracy: number;
  }>;
  locations: Array<{
    id: string;
    name: string;
    city: string;
    state: string;
    country: string;
    ai_suggested_city: string;
    ai_suggested_state: string;
    ai_suggested_country: string;
    validation_confidence: number;
    latitude: number;
    longitude: number;
    address_matched: number;
    status: 'correct' | 'incorrect' | 'pending';
  }>;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  last_updated: string;
}

interface LatLongValidationData {
  overview: {
    total_locations: number;
    validated_locations: number;
    pending_locations: number;
    validation_rate: number;
  };
  locations?: Array<{
    id: number;
    name: string;
    city: string;
    state: string;
    country: string;
    zipcode: string;
    latitude_wf: number | null;
    longitude_wf: number | null;
    is_lat_long_validate: number;
    customer_id: number;
    staff_id: number;
    staff_name: string;
  }>;
  last_updated: string;
}

const Diagnosys: React.FC = () => {
  // Add custom styles for spinner color
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-select-loading .ant-select-arrow .anticon-loading {
        color: #002affff !important;
      }
      .custom-select-loading .ant-select-arrow .anticon-loading svg {
        color: #000dffff !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [latLongData, setLatLongData] = useState<LatLongValidationData | null>(null);
  const [latLongLoading, setLatLongLoading] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [editCountries, setEditCountries] = useState<any[]>([]);
  const [editStates, setEditStates] = useState<any[]>([]);
  const [editCities, setEditCities] = useState<any[]>([]);
  const [filters, setFilters] = useState<DiagnosysFilters>({
    status: 'all',
    country: '',
    state: '',
    search: '',
    page: 1,
    pageSize: 10
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [editForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);
  const [editStatesLoading, setEditStatesLoading] = useState(false);
  const [editCitiesLoading, setEditCitiesLoading] = useState(false);
  const [customStateValue, setCustomStateValue] = useState('');
  const [customCityValue, setCustomCityValue] = useState('');
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  // Load dashboard data with filters
  const loadDashboardData = async (currentFilters = filters) => {
    // Allow state filter calls even if loading is in progress
    
    setLoading(true);
    setTableLoading(true); // Also set table loading for visual feedback
    
    try {
      const params = new URLSearchParams();
      if (currentFilters.status !== 'all') params.append('status', currentFilters.status);
      if (currentFilters.country) params.append('country', currentFilters.country);
      if (currentFilters.state) params.append('state', currentFilters.state);
      if (currentFilters.search) params.append('search', currentFilters.search);
      params.append('page', currentFilters.page.toString());
      params.append('page_size', currentFilters.pageSize.toString());

      const response = await api.get(`/api/diagnosys/visualization-stats/?${params.toString()}`);
      
      if (response.data.status === 'success') {
        // Process the response data
        let processedData = response.data.data;
        
        // Check if filter_data exists in the response and process them
        if (processedData.filter_data && Array.isArray(processedData.filter_data)) {
          // Use filter_data directly without zipcode extraction
          
          processedData = {
            ...processedData,
            locations: processedData.filter_data,
            pagination: processedData.pagination || {
              current: currentFilters.page,
              pageSize: currentFilters.pageSize,
              total: processedData.filter_data.length,
              totalPages: Math.ceil(processedData.filter_data.length / currentFilters.pageSize)
            }
          };
        } else if (processedData.locations && Array.isArray(processedData.locations)) {
          // Fallback: if locations exist in the response (legacy format)
          
          processedData = {
            ...processedData,
            locations: processedData.locations,
            pagination: processedData.pagination || {
              current: currentFilters.page,
              pageSize: currentFilters.pageSize,
              total: processedData.locations.length,
              totalPages: Math.ceil(processedData.locations.length / currentFilters.pageSize)
            }
          };
        } else {
          // If no filter_data or locations data, create empty array
          processedData = {
            ...processedData,
            locations: [],
            pagination: processedData.pagination || {
              current: currentFilters.page,
              pageSize: currentFilters.pageSize,
              total: 0,
              totalPages: 0
            }
          };
        }
        
        setDashboardData(processedData);
        setTimeout(() => initializeCharts(processedData), 100);
      } else {
        message.error('Failed to load dashboard data');
        console.error('Dashboard API returned error:', response.data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      message.error('Error loading dashboard data. Please try again.');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };


  // Handle status filter change
  const handleStatusFilter = (status: 'all' | 'correct' | 'incorrect') => {
    const newFilters = { ...filters, status, page: 1 };
    setFilters(newFilters);
    // Only load dashboard data (which includes table data)
    loadDashboardData(newFilters);
  };

  // Handle country filter change
  const handleCountryChange = (country: string) => {
    const newFilters = { ...filters, country, state: '', page: 1 };
    setFilters(newFilters);
    if (country) {
      loadStates(country);
    } else {
      setStates([]);
    }
    // Only load dashboard data (which includes table data)
    loadDashboardData(newFilters);
  };

  // Handle state filter change
  const handleStateChange = (state: string) => {
    const newFilters = { ...filters, state, page: 1 };
    setFilters(newFilters);
    // Only load dashboard data (which includes table data)
    loadDashboardData(newFilters);
  };

  // Handle search change with debouncing
  const debouncedSearch = useCallback(
    debounce((searchValue: string, currentFilters: DiagnosysFilters) => {
      const newFilters = { ...currentFilters, search: searchValue, page: 1 };
      setFilters(newFilters);
      // Only load dashboard data (which includes table data)
      loadDashboardData(newFilters);
    }, 500),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters(prev => {
      const updatedFilters = { ...prev, search: value };
      debouncedSearch(value, updatedFilters);
      return updatedFilters;
    });
  };

  // Clear all filters
  const clearFilters = () => {
    const newFilters = {
      status: 'all' as const,
      country: '',
      state: '',
      search: '',
      page: 1,
      pageSize: 10
    };
    setFilters(newFilters);
    setStates([]);
    // Only load dashboard data (which includes table data)
    loadDashboardData(newFilters);
  };

  // Load countries for dropdown
  const loadCountries = async () => {
    setCountriesLoading(true);
    try {
      const response = await api.get('/api/countries/');
      const countriesData = response.data?.data ?? response.data;
      setCountries(countriesData.map((country: any) => country.short_name || country.name));
    } catch (error) {
      console.error('Error loading countries:', error);
    } finally {
      setCountriesLoading(false);
    }
  };

  // Load states for dropdown
  const loadStates = async (country: string) => {
    setStatesLoading(true);
    try {
      const response = await api.get(`/api/states/?country=${encodeURIComponent(country)}`);
      setStates(response.data);
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      setStatesLoading(false);
    }
  };

  // Load cities for dropdown
  const loadCities = async (state: string, country: string) => {
    try {
      const response = await api.post('/api/cities/', {
        region: state,
        country: country
      });
      return response.data;
    } catch (error) {
      console.error('Error loading cities:', error);
      return [];
    }
  };

  // Load countries for edit form
  const loadEditCountries = async () => {
    try {
      const response = await api.get('/api/countries/');
      const countriesData = response.data?.data ?? response.data;
      setEditCountries(countriesData);
      return countriesData;
    } catch (error) {
      console.error('Error loading edit countries:', error);
      message.error('Failed to load countries');
      return [];
    }
  };

  // Load countries for edit form if needed
  // Load states for edit form and return data
  const loadEditStatesAndReturn = async (countryId: string): Promise<any[]> => {
    setEditStatesLoading(true);
    try {
      // Find country name from countryId
      const selectedCountry = editCountries.find(country => country.country_id === countryId);
      const countryName = selectedCountry ? (selectedCountry.short_name || selectedCountry.name) : '';
      
      if (countryName) {
        const response = await api.get(`/api/states/?country=${encodeURIComponent(countryName)}`);
        // Clean up state data - remove extra spaces and filter out invalid entries
        const cleanStates = response.data
          .map((state: any) => {
            if (typeof state === 'string') {
              return {
                state_id: state.trim(),
                name: state.trim()
              };
            }
            return {
              state_id: state.state_id || state.name,
              name: (state.name || state).trim()
            };
          })
          .filter((state: any) => state.name && state.name.length > 0 && !state.name.includes('?') && !state.name.includes('(') && state.name !== 'Canada');
        
        setEditStates(cleanStates);
        return cleanStates;
      }
      return [];
    } catch (error) {
      console.error('Error loading edit states:', error);
      message.error('Failed to load states');
      return [];
    } finally {
      setEditStatesLoading(false);
    }
  };

  // Load cities for edit form and return data
  const loadEditCitiesAndReturn = async (stateId: string): Promise<any[]> => {
    setEditCitiesLoading(true);
    try {
      // Find state name from stateId
      const selectedState = editStates.find(state => state.state_id === stateId);
      const stateName = selectedState ? selectedState.name : '';
      
      // Find country name from the selected country in the form
      const countryId = editForm.getFieldValue('country');
      const selectedCountry = editCountries.find(country => country.country_id === countryId);
      const countryName = selectedCountry ? (selectedCountry.short_name || selectedCountry.name) : '';
      
      if (stateName && countryName) {
        // Use POST method with both region (state) and country
        const response = await api.post('/api/cities/', {
          region: stateName,
          country: countryName
        });
        // Clean up city data similar to states
        const cleanCities = response.data
          .map((city: any) => {
            if (typeof city === 'string') {
              return {
                city_id: city.trim(),
                name: city.trim()
              };
            }
            return {
              city_id: city.city_id || city.name,
              name: (city.name || city).trim()
            };
          })
          .filter((city: any) => city.name && city.name.length > 0);
        
        setEditCities(cleanCities);
        return cleanCities;
      }
      return [];
    } catch (error) {
      console.error('Error loading edit cities:', error);
      message.error('Failed to load cities');
      return [];
    } finally {
      setEditCitiesLoading(false);
    }
  };

  // Handle edit country change
  const handleEditCountryChange = async (countryId: string) => {
    editForm.setFieldsValue({ state: undefined, city: undefined });
    setEditStates([]);
    setEditCities([]);
    
    if (countryId) {
      try {
        await loadEditStatesAndReturn(countryId);
      } catch (error) {
        console.error('Error loading states for country change:', error);
      }
    }
  };

  // Handle edit state change
  const handleEditStateChange = async (stateId: string) => {
    editForm.setFieldsValue({ city: undefined });
    setEditCities([]);
    
    if (stateId) {
      try {
        await loadEditCitiesAndReturn(stateId);
      } catch (error) {
        console.error('Error loading cities for state change:', error);
      }
    }
  };

  // Initialize charts
  const initializeCharts = (_data: DashboardData) => {
    // This would initialize Chart.js charts
    // For now, we'll use placeholder logic
  };

  // Load lat-long validation data
  const loadLatLongValidationData = async () => {
    setLatLongLoading(true);
    try {
      const response = await api.get('/api/diagnosys/lat-long-validation-stats/');
      
      if (response.data.status === 'success') {
        // Check if response.data.data is an array of locations or already formatted statistics
        if (Array.isArray(response.data.data)) {
          // If it's an array of locations, calculate statistics
          const locations = response.data.data;
          const totalLocations = locations.length;
          const validatedLocations = locations.filter((loc: any) => loc.is_lat_long_validate === 1).length;
          const pendingLocations = locations.filter((loc: any) => loc.is_lat_long_validate === 0).length;
          const validationRate = totalLocations > 0 ? ((validatedLocations / totalLocations) * 100).toFixed(2) : 0;
          
          setLatLongData({
            overview: {
              total_locations: totalLocations,
              validated_locations: validatedLocations,
              pending_locations: pendingLocations,
              validation_rate: parseFloat(validationRate.toString())
            },
            locations: locations,
            last_updated: new Date().toISOString()
          });
        } else {
          // If it's already formatted statistics, use as is
          setLatLongData(response.data.data);
        }
      } else {
        message.error('Failed to load lat-long validation data');
      }
    } catch (error) {
      console.error('Error loading lat-long validation data:', error);
      message.error('Error loading lat-long validation data. Please try again.');
    } finally {
      setLatLongLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    
    // Only load dashboard data (which includes all table data and pagination)
    const initializeData = async () => {
      try {
        // Load dashboard data (includes overview stats and locations)
        await loadDashboardData();
      } catch (error) {
        console.error('Error initializing data:', error);
        message.error('Failed to initialize dashboard data');
      }
    };
    
    initializeData();
    loadCountries();
    // Pre-load edit countries for faster modal opening
    loadEditCountries();
    // Load lat-long validation data
    loadLatLongValidationData();
  }, []); // Empty dependency array to ensure this runs only once

  // Handle table pagination change
  const handleTableChange = (pagination: any) => {
    const newFilters = {
      ...filters,
      page: pagination.current,
      pageSize: pagination.pageSize
    };
    setFilters(newFilters);
    // Only load dashboard data (which includes table data and pagination)
    loadDashboardData(newFilters);
  };

  // Calculate address matching percentage
  const calculateMatchingPercentage = (address1: string, address2: string) => {
    if (!address1 || !address2) return 0;
    
    const normalize = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
    const addr1 = normalize(address1);
    const addr2 = normalize(address2);
    
    if (addr1 === addr2) return 100;
    
    // Simple similarity calculation
    const longer = addr1.length > addr2.length ? addr1 : addr2;
    const shorter = addr1.length > addr2.length ? addr2 : addr1;
    
    if (longer.length === 0) return 100;
    
    const editDistance = (s1: string, s2: string) => {
      const matrix = Array(s2.length + 1).fill(null).map(() => Array(s1.length + 1).fill(null));
      
      for (let i = 0; i <= s1.length; i++) matrix[0][i] = i;
      for (let j = 0; j <= s2.length; j++) matrix[j][0] = j;
      
      for (let j = 1; j <= s2.length; j++) {
        for (let i = 1; i <= s1.length; i++) {
          const indicator = s1[i - 1] === s2[j - 1] ? 0 : 1;
          matrix[j][i] = Math.min(
            matrix[j][i - 1] + 1,
            matrix[j - 1][i] + 1,
            matrix[j - 1][i - 1] + indicator
          );
        }
      }
      
      return matrix[s2.length][s1.length];
    };
    
    const distance = editDistance(shorter, longer);
    return Math.round((longer.length - distance) / longer.length * 100);
  };

  // Calculate overall location matching percentage (city, state, country, address)
  const calculateLocationMatchingPercentage = (record: any) => {
    const dbCity = record.city || '';
    const dbState = record.state || '';
    const dbCountry = record.country || '';
    const dbAddress = record.address || '';
    
    const websiteCity = record.website_found_city || '';
    const websiteState = record.website_found_state || '';
    const websiteCountry = record.website_found_country || '';
    const websiteAddress = record.website_found_address || '';
    
    // If no website data available, return 0
    if (!websiteCity && !websiteState && !websiteCountry && !websiteAddress) {
      return 0;
    }
    
    // Calculate individual field matches
    const cityMatch = calculateMatchingPercentage(dbCity, websiteCity);
    const stateMatch = calculateMatchingPercentage(dbState, websiteState);
    const countryMatch = calculateMatchingPercentage(dbCountry, websiteCountry);
    const addressMatch = calculateMatchingPercentage(dbAddress, websiteAddress);
    
    // Weighted average (address has higher weight)
    const weights = {
      city: 0.2,
      state: 0.2,
      country: 0.2,
      address: 0.4
    };
    
    const weightedScore = (
      cityMatch * weights.city +
      stateMatch * weights.state +
      countryMatch * weights.country +
      addressMatch * weights.address
    );
    
    return Math.round(weightedScore);
  };

  // Handle edit location - UPDATED VERSION
  const handleEditLocation = async (record: any) => {

    setEditingRecord(record);
    setEditModalVisible(true);
    
    // Reset dependent dropdowns first
    setEditStates([]);
    setEditCities([]);
    
    // Set initial form values - only address and zipcode initially
    const zipcodeValue = record.zipcode || record.zip || '';
    
    editForm.setFieldsValue({
      country: undefined,
      state: undefined,
      city: undefined,
      address: record.address || '',
      zipcode: zipcodeValue
    });
    
    
    // Load data asynchronously after modal is open
    setTimeout(async () => {
      try {
        // Ensure edit countries are loaded first
        let countriesData = editCountries;
        if (countriesData.length === 0) {
          countriesData = await loadEditCountries();
        }
        
        // Load dependent data asynchronously if country is available
        if (record.country_id || record.country) {
          let countryIdToUse = record.country_id;
          let countryNameToUse = record.country;
          
          // If no country_id, try to find it by country name
          if (!countryIdToUse && record.country) {
            const matchedCountry = countriesData.find(country => {
              const countryName = country.short_name || country.name;
              return countryName && countryName.toLowerCase() === record.country.toLowerCase();
            });
            
            if (matchedCountry) {
              countryIdToUse = matchedCountry.country_id;
              countryNameToUse = matchedCountry.short_name || matchedCountry.name;
            } else {
              console.warn('Country not found in dropdown:', record.country);
              message.warning(`Country "${record.country}" not found in dropdown`);
            }
          } else if (countryIdToUse) {
            // Find country name from countryId
            const selectedCountry = countriesData.find(country => country.country_id === countryIdToUse);
            countryNameToUse = selectedCountry ? (selectedCountry.short_name || selectedCountry.name) : record.country;
          }
          
          if (countryIdToUse && countryNameToUse) {
            // Set country first
            editForm.setFieldsValue({ country: countryIdToUse });
            
            // Load states for the country using simple loadStates approach
            if (countryNameToUse) {
              try {
                // Set loading state for states
                setEditStatesLoading(true);
                
                const response = await api.get(`/api/states/?country=${encodeURIComponent(countryNameToUse)}`);
                const statesData = response.data
                  .map((state: any) => {
                    if (typeof state === 'string') {
                      return {
                        state_id: state.trim(),
                        name: state.trim()
                      };
                    }
                    return {
                      state_id: state.state_id || state.name,
                      name: (state.name || state).trim()
                    };
                  })
                  .filter((state: any) => state.name && state.name.length > 0 && !state.name.includes('?') && !state.name.includes('(') && state.name !== 'Canada');
                
                setEditStates(statesData);
                setEditStatesLoading(false);
                
                // Try to set the state value
                if ((record.state || record.state_id) && statesData.length > 0) {
                  let stateIdToSet = null;
                  let stateNameToUse = record.state;
                  
                  // First try to match by state_id
                  if (record.state_id) {
                    const stateById = statesData.find((state: any) => state.state_id === record.state_id);
                    if (stateById) {
                      stateIdToSet = record.state_id;
                      stateNameToUse = stateById.name;
                    }
                  }
                  
                  // If not found by ID, try to match by name
                  if (!stateIdToSet && record.state) {
                    const stateByName = statesData.find((state: any) => 
                      state.name && state.name.toLowerCase().trim() === record.state.toLowerCase().trim()
                    );
                    if (stateByName) {
                      stateIdToSet = stateByName.state_id;
                      stateNameToUse = stateByName.name;
                    }
                  }
                  
                  if (stateIdToSet && stateNameToUse) {
                    // Set state value
                    editForm.setFieldsValue({ state: stateIdToSet });
                    
                    // Load cities using the simple loadCities approach
                    setEditCitiesLoading(true);
                    
                    const citiesData = await loadCities(stateNameToUse, countryNameToUse);
                    const cleanCities = citiesData
                      .map((city: any) => {
                        if (typeof city === 'string') {
                          return {
                            city_id: city.trim(),
                            name: city.trim()
                          };
                        }
                        return {
                          city_id: city.city_id || city.name,
                          name: (city.name || city).trim()
                        };
                      })
                      .filter((city: any) => city.name && city.name.length > 0);
                    
                    setEditCities(cleanCities);
                    setEditCitiesLoading(false);
                    
                    // Try to set the city value
                    if ((record.city || record.city_id) && cleanCities.length > 0) {
                      let cityIdToSet: string | null = null;
                      
                      // First try to match by city_id
                      if (record.city_id) {
                        const cityById = cleanCities.find((city: any) => city.city_id === record.city_id);
                        if (cityById) {
                          cityIdToSet = record.city_id;
                        }
                      }
                      
                      // If not found by ID, try to match by name
                      if (!cityIdToSet && record.city) {
                        const cityByName = cleanCities.find((city: any) => 
                          city.name && city.name.toLowerCase().trim() === record.city.toLowerCase().trim()
                        );
                        if (cityByName) {
                          cityIdToSet = cityByName.city_id;
                        }
                      }
                      
                      if (cityIdToSet) {
                        // Set city value
                        editForm.setFieldsValue({ city: cityIdToSet });
                      } else {
                        console.warn('City not found in dropdown:', record.city);
                      }
                    }
                  } else {
                    console.warn('State not found in dropdown:', record.state);
                  }
                }
              } catch (error) {
                console.error('Error loading states:', error);
                setEditStatesLoading(false);
                setEditCitiesLoading(false);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading dependent data:', error);
        message.error('Error loading location data. Please try again.');
        setEditStatesLoading(false);
        setEditCitiesLoading(false);
      }
    }, 100);
  };

  // Handle edit form submission
  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);

      
      // Use the ID directly as it's already a number
      const locationId = editingRecord.id;
      
      
      // Prepare the update payload
      const updatePayload = {
        city: values.city,
        state: values.state,
        country: values.country,
        address: values.address,
        pin: values.zipcode || ''
      };
      

      const response = await api.put(`/api/diagnosys/locations/${locationId}/`, updatePayload);
      

      if (response.data.status === 'success') {
        message.success('Location updated successfully');
        setEditModalVisible(false);
        setEditingRecord(null);
        editForm.resetFields();
        // Reload dashboard data (which includes table data)
        loadDashboardData(filters);
      } else {
        console.error('Update failed with response:', response.data);
        message.error(response.data.message || 'Failed to update location');
      }
    } catch (error: any) {
      console.error('Error updating location:', error);
      
      // More detailed error handling
      if (error?.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
        
        if (error.response.status === 404) {
          message.error('Location not found. Please refresh and try again.');
        } else if (error.response.status === 400) {
          message.error('Invalid data provided. Please check all fields.');
        } else if (error.response.status === 500) {
          message.error('Server error. Please try again later.');
        } else {
          message.error(`Error updating location: ${error.response.data?.message || error.message || 'Unknown error'}`);
        }
      } else if (error?.request) {
        console.error('Network error:', error.request);
        message.error('Network error. Please check your connection and try again.');
      } else {
        console.error('Error message:', error?.message);
        message.error(`Error updating location: ${error?.message || 'Unknown error occurred'}`);
      }
    } finally {
      setEditLoading(false);
    }
  };

  // Handle edit modal cancel
  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingRecord(null);
    editForm.resetFields();
  };

  // Table columns for location validation
  const columns: ColumnsType<any> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left', // Make Name column sticky
      render: (text: string, record: any) => {
        if (record.company_website) {
          return (
            <a 
              href={record.company_website.startsWith('http') ? record.company_website : `https://${record.company_website}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1890ff', textDecoration: 'none' }}
            >
              {text || '-'}
            </a>
          );
        }
        return text || '-';
      },
    },
    
    {
      title: 'Sales Rep',
      dataIndex: 'staff_name',
      key: 'staff_name',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 250,
      render: (text: string) => text || '-',
    },
    {
      title: 'Zip Code',
      dataIndex: 'zipcode',
      key: 'zipcode',
      width: 100,
      render: (text: string) => text || '-',
    },
    {
      title: 'Website City',
      dataIndex: 'website_found_city',
      key: 'website_city',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'Website State',
      dataIndex: 'website_found_state',
      key: 'website_state',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'Website Country',
      dataIndex: 'website_found_country',
      key: 'website_country',
      width: 150,
      render: (text: string) => text || '-',
    },
    {
      title: 'Website Address',
      dataIndex: 'website_found_address',
      key: 'website_address',
      width: 250,
      render: (text: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{text || '-'}</span>
          {record.website_validation_notes && (
            <Tooltip title={record.website_validation_notes} placement="top">
              <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Confidence',
      key: 'confidence',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const percentage = calculateLocationMatchingPercentage(record);
        const color = percentage >= 80 ? '#52c41a' : percentage >= 60 ? '#faad14' : '#ff4d4f';
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Progress
              percent={percentage}
              size="small"
              strokeColor={color}
              style={{ width: '60px' }}
            />
            <span style={{ color, fontWeight: 'bold', fontSize: '12px' }}>
              {percentage}%
            </span>
          </div>
        );
      },
      sorter: (a, b) => {
        const percentageA = calculateLocationMatchingPercentage(a);
        const percentageB = calculateLocationMatchingPercentage(b);
        return percentageA - percentageB;
      },
    },
    {
      title: 'Coordinates',
      key: 'coordinates',
      width: 150,
      render: (_, record) => {
        const lat = typeof record.latitude === 'number' ? record.latitude.toFixed(4) : record.latitude || 'N/A';
        const lng = typeof record.longitude === 'number' ? record.longitude.toFixed(4) : record.longitude || 'N/A';
        return `${lat}, ${lng}`;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEditLocation(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  if (loading && !dashboardData) {
    return (
      <PageContainer>
        <Header>
          <Title level={1}>Location Validation Dashboard</Title>
          <Text>Real-time analysis of location accuracy based on coordinates</Text>
        </Header>
        <Container>
          <LoadingContainer>
            <div className="spinner"></div>
            <p>Loading dashboard data...</p>
          </LoadingContainer>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <Title level={1}>Location Validation Dashboard</Title>
        <Text style={{color: 'white'}}>Real-time analysis of location accuracy based on coordinates</Text>
      </Header>
      <Container>
        {/* Stats Cards */}
        <StatsGrid>
          <StatCard 
            cardType="total"
            isActive={filters.status === 'all'}
            isClickable={true}
            onClick={() => handleStatusFilter('all')}
          >
            <Statistic
              title={
                <>
                  Total Locations
                  <span>
                    <DashboardOutlined style={{fontSize: '20px'}}/>
                  </span>
                </>
                }
              value={dashboardData?.overview.total_locations || 0}
              suffix="All locations with coordinates and address"
            />
          </StatCard>
          
          <StatCard 
            cardType="correct"
            isActive={filters.status === 'correct'}
            isClickable={true}
            onClick={() => handleStatusFilter('correct')}
          >
            <Statistic
              title={
                <>
                  Correct Locations
                  <span>
                    <CheckCircleOutlined style={{fontSize: '20px'}}/>
                  </span>
                </>
              }
              value={dashboardData?.overview.correct_locations || 0}
              suffix="Matching Address (Shipping Address = Website Address)"
            />
          </StatCard>
          
          <StatCard 
            cardType="incorrect"
            isActive={filters.status === 'incorrect'}
            isClickable={true}
            onClick={() => handleStatusFilter('incorrect')}
          >
            <Statistic
              title={
                <>
                  Incorrect Locations
                  <span>
                    <CloseCircleOutlined style={{fontSize: '20px'}}/>
                  </span>
                </>
              }
              value={dashboardData?.overview.incorrect_locations || 0}
              suffix="Mismatched Address (Shipping Address != Website Address or website not open)"
            />
          </StatCard>
          
          <StatCard cardType="pending">
            <Statistic
              title={
                <>
                  Not Validated
                  <span>
                    <WarningOutlined style={{fontSize: '20px'}}/>
                  </span>
                </>
              }
              value={dashboardData?.overview.not_validated || 0}
              suffix="Pending validation"
            />
          </StatCard>
        </StatsGrid>

        {/* Lat-Long Validation Stats Cards */}
        <div style={{ marginBottom: '1rem' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '1rem' }}>
            Coordinate Validation Statistics
          </Title>
        </div>
        <StatsGrid>
          {latLongLoading ? (
            <>
              <StatCard>
                <Skeleton active />
              </StatCard>
              <StatCard>
                <Skeleton active />
              </StatCard>
              <StatCard>
                <Skeleton active />
              </StatCard>
              <StatCard>
                <Skeleton active />
              </StatCard>
            </>
          ) : (
            <>
              <StatCard cardType="total">
                <Statistic
                  title={
                    <>
                    Total Locations
                    <span>
                      <DashboardOutlined style={{fontSize: '20px'}}/>
                    </span>
                    </>
                  }
                  value={latLongData?.overview.total_locations || 0}
                  suffix="All locations in database"
                />
              </StatCard>
              
              <StatCard cardType="correct">
                <Statistic
                  title={
                    <>
                      Validated Locations
                      <span>
                        <CheckCircleOutlined style={{fontSize: '20px'}}/>
                      </span>
                    </>
                  }
                  value={latLongData?.overview.validated_locations || 0}
                  suffix="Locations with validated coordinates"
                />
              </StatCard>
              
              <StatCard cardType="pending">
                <Statistic
                  title={
                    <>
                      Pending Locations
                      <span>
                        <WarningOutlined style={{fontSize: '20px'}}/>
                      </span>
                    </>
                  }
                  value={latLongData?.overview.pending_locations || 0}
                  suffix="Locations pending coordinate validation"
                />
              </StatCard>
              
              <StatCard cardType="total">
                <Statistic
                  title={
                    <>
                      Validation Rate
                      <span>
                        <DashboardOutlined style={{fontSize: '20px'}}/>
                      </span>
                    </>
                  }
                  value={latLongData?.overview.validation_rate || 0}
                  suffix="%"
                />
              </StatCard>
            </>
          )}
        </StatsGrid>

        {/* Filter Bar */}
        <FilterBar>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search locations..."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={handleSearchChange}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Select Country"
                value={filters.country || undefined}
                onChange={handleCountryChange}
                allowClear
                showSearch
                style={{ width: '100%' }}
                suffixIcon={<GlobalOutlined />}
                loading={countriesLoading}
                notFoundContent={countriesLoading ? "Loading countries..." : "No countries found"}
              >
                {countries.map(country => (
                  <Option key={country} value={country}>{country}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Select State"
                value={filters.state || undefined}
                onChange={handleStateChange}
                allowClear
                showSearch
                style={{ width: '100%' }}
                suffixIcon={<EnvironmentOutlined />}
                disabled={!filters.country}
                loading={statesLoading}
                notFoundContent={statesLoading ? "Loading states..." : "No states found"}
              >
                {states.map(state => (
                  <Option key={state} value={state}>{state}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                icon={<ClearOutlined />}
                onClick={clearFilters}
                style={{ width: '100%' }}
              >
                Clear Filters
              </Button>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                type="primary"
                icon={loading ? <SyncOutlined spin /> : <ReloadOutlined />}
                onClick={() => loadDashboardData(filters)}
                loading={loading}
                style={{ width: '100%' }}
              >
                Refresh
              </Button>
            </Col>
          </Row>
          
          {/* Active Filter Tags */}
          {(filters.status !== 'all' || filters.country || filters.state || filters.search) && (
            <FilterTags>
              <Text type="secondary" style={{ fontSize: '0.85rem', marginRight: '0.5rem' }}>
                Active Filters:
              </Text>
              {filters.status !== 'all' && (
                <Tag
                  closable
                  onClose={() => handleStatusFilter('all')}
                  color={filters.status === 'correct' ? 'success' : 'error'}
                >
                  Status: {filters.status === 'correct' ? 'Correct' : 'Incorrect'}
                </Tag>
              )}
              {filters.country && (
                <Tag
                  closable
                  onClose={() => handleCountryChange('')}
                  color="blue"
                >
                  Country: {filters.country}
                </Tag>
              )}
              {filters.state && (
                <Tag
                  closable
                  onClose={() => handleStateChange('')}
                  color="green"
                >
                  State: {filters.state}
                </Tag>
              )}
              {filters.search && (
                <Tag
                  closable
                  onClose={() => {
                    const newFilters = { ...filters, search: '', page: 1 };
                    setFilters(newFilters);
                    loadDashboardData(newFilters);
                  }}
                  color="orange"
                >
                  Search: {filters.search}
                </Tag>
              )}
            </FilterTags>
          )}
        </FilterBar>

        {/* Locations Table */}
        <TableContainer 
          title="Location Validation Results">
          <Table
            columns={columns}
            dataSource={dashboardData?.locations || []}
            rowKey="id"
            pagination={{ 
              current: dashboardData?.pagination?.current || 1,
              pageSize: dashboardData?.pagination?.pageSize || 10,
              total: dashboardData?.pagination?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
            }}
            scroll={{ x: 'max-content' }}
            loading={tableLoading}
            onChange={handleTableChange}
            size="small"
          />
        </TableContainer>

        {/* Controls */}
        {/* <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <ActionButton 
            type="primary" 
            icon={loading || latLongLoading ? <SyncOutlined spin /> : <ReloadOutlined />}
            onClick={() => {
              loadDashboardData(filters);
              loadLatLongValidationData();
            }}
            loading={loading || latLongLoading}
          >
            Refresh Dashboard
          </ActionButton>
        </div> */}

        <LastUpdate>
          Last updated: {dashboardData?.last_updated || '-'}
        </LastUpdate>

        {/* Edit Location Modal */}
        <Modal
          title={`Edit Location - ${editingRecord?.name || ''}`}
          open={editModalVisible}
          onOk={handleEditSubmit}
          onCancel={handleEditCancel}
          confirmLoading={editLoading}
          width={600}
          okText="Save Changes"
          cancelText="Cancel"
          okButtonProps={{
            icon: <SaveOutlined />,
            loading: editLoading
          }}
          cancelButtonProps={{
            icon: <CloseOutlined />
          }}
        >
          <Form
            form={editForm}
            layout="vertical"
            style={{ marginTop: '1rem' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Country"
                  name="country"
                  rules={[{ required: true, message: 'Please select country' }]}
                >
                  <Select
                    placeholder="Select Country"
                    showSearch
                    allowClear
                    onChange={handleEditCountryChange}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {editCountries.map(country => (
                      <Option key={country.country_id} value={country.country_id}>
                        {country.short_name || country.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="State"
                  name="state"
                  rules={[{ required: true, message: 'Please select state' }]}
                >
                  <Select
                    placeholder="Select State"
                    showSearch
                    allowClear
                    onChange={handleEditStateChange}
                    style={{ width: '100%' }}
                    disabled={!editForm.getFieldValue('country')}
                    loading={editStatesLoading}
                    notFoundContent={editStatesLoading ? <div style={{ textAlign: 'center', padding: '8px' }}><span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Loading states...</span></div> : "No states found"}
                    className="custom-select-loading"
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    onSearch={(value) => setCustomStateValue(value)}
                    dropdownRender={(menu) => (
                      <div>
                        {menu}
                        {customStateValue && !editStates.some(state => 
                          state.name.toLowerCase() === customStateValue.toLowerCase()
                        ) && (
                          <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                            <Button
                              type="text"
                              icon={<span style={{ color: '#52c41a' }}>+</span>}
                              onClick={() => {
                                const newState = {
                                  state_id: `${customStateValue}`,
                                  name: customStateValue
                                };
                                setEditStates(prev => [...prev, newState]);
                                editForm.setFieldsValue({ state: newState.state_id });
                                setCustomStateValue('');
                              }}
                              style={{ width: '100%', textAlign: 'left' }}
                            >
                              Add "{customStateValue}"
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  >
                    {editStates.map(state => (
                      <Option key={state.state_id} value={state.state_id}>
                        {state.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: true, message: 'Please select city' }]}
                >
                  <Select
                    placeholder="Select City"
                    showSearch
                    allowClear
                    style={{ width: '100%' }}
                    disabled={!editForm.getFieldValue('state')}
                    loading={editCitiesLoading}
                    notFoundContent={editCitiesLoading ? <div style={{ textAlign: 'center', padding: '8px' }}><span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Loading cities...</span></div> : "No cities found"}
                    className="custom-select-loading"
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    onSearch={(value) => setCustomCityValue(value)}
                    dropdownRender={(menu) => (
                      <div>
                        {menu}
                        {customCityValue && !editCities.some(city => 
                          city.name.toLowerCase() === customCityValue.toLowerCase()
                        ) && (
                          <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                            <Button
                              type="text"
                              icon={<span style={{ color: '#52c41a' }}>+</span>}
                              onClick={() => {
                                const newCity = {
                                  city_id: `${customCityValue}`,
                                  name: customCityValue
                                };
                                setEditCities(prev => [...prev, newCity]);
                                editForm.setFieldsValue({ city: newCity.city_id });
                                setCustomCityValue('');
                              }}
                              style={{ width: '100%', textAlign: 'left' }}
                            >
                              Add "{customCityValue}"
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  >
                    {editCities.map(city => (
                      <Option key={city.city_id} value={city.city_id}>
                        {city.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Zip Code"
                  name="zipcode"
                  rules={[{ required: true, message: 'Please enter zip code' }]}
                >
                  <Input 
                    placeholder="Enter zip code"
                    maxLength={20}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label="Address"
              name="address"
              rules={[{ required: true, message: 'Please enter address' }]}
            >
              <Input.TextArea 
                rows={3} 
                placeholder="Enter complete address"
              />
            </Form.Item>
            
            {editingRecord && (
              <div style={{ 
                background: '#f5f5f5', 
                padding: '1rem', 
                borderRadius: '8px',
                marginTop: '1rem'
              }}>
                <Text strong style={{ color: '#666' }}>Current Website Address:</Text>
                <br />
                <Text style={{ fontSize: '0.9rem' }}>
                  {editingRecord.website_found_address || 'No website address found'}
                </Text>
                {editingRecord.website_validation_notes && (
                  <>
                    <br />
                    <Text strong style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                      Validation Notes:
                    </Text>
                    <Text style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                      {editingRecord.website_validation_notes}
                    </Text>
                  </>
                )}
              </div>
            )}
          </Form>
        </Modal>
      </Container>
    </PageContainer>
  );
};

export default Diagnosys;
